import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'app-theme';
  private themeSubject: BehaviorSubject<Theme>;
  private systemThemeMediaQuery: MediaQueryList;

  constructor() {
    // Get initial theme from localStorage or default to 'system'
    const savedTheme = this.getSavedTheme();
    this.themeSubject = new BehaviorSubject<Theme>(savedTheme);

    // Set up media query listener for system theme changes
    this.systemThemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemThemeMediaQuery.addEventListener('change', () => {
      if (this.themeSubject.value === 'system') {
        this.applyTheme('system');
      }
    });

    // Apply initial theme
    this.applyTheme(savedTheme);
  }

  get theme$(): Observable<Theme> {
    return this.themeSubject.asObservable();
  }

  get currentTheme(): Theme {
    return this.themeSubject.value;
  }

  setTheme(theme: Theme): void {
    this.themeSubject.next(theme);
    this.saveTheme(theme);
    this.applyTheme(theme);
  }

  private getSavedTheme(): Theme {
    const saved = localStorage.getItem(this.THEME_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      return saved;
    }
    return 'system';
  }

  private saveTheme(theme: Theme): void {
    localStorage.setItem(this.THEME_KEY, theme);
  }

  private applyTheme(theme: Theme): void {
    const isDark = this.isDarkMode(theme);
    const htmlElement = document.documentElement;

    if (isDark) {
      htmlElement.classList.add('dark-theme');
      htmlElement.classList.remove('light-theme');
    } else {
      htmlElement.classList.add('light-theme');
      htmlElement.classList.remove('dark-theme');
    }

    // Store the actual resolved theme (light/dark) as a data attribute
    htmlElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }

  private isDarkMode(theme: Theme): boolean {
    if (theme === 'dark') {
      return true;
    } else if (theme === 'light') {
      return false;
    } else {
      // System theme - check media query
      return this.systemThemeMediaQuery.matches;
    }
  }

  getEffectiveTheme(): 'light' | 'dark' {
    return this.isDarkMode(this.currentTheme) ? 'dark' : 'light';
  }
}

